"use client";

import { c as _c } from "react/compiler-runtime";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type FC, useEffect, useId, useMemo, useState, useSyncExternalStore } from "react";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import HoverCard from "@/components/utils/tooltip/HoverCard";
import type { ApiWave } from "@/generated/models/ApiWave";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { Time } from "@/helpers/time";
import { calculateTimeLeft } from "@/helpers/waves/time.utils";
import type { ApprovalWaveCloseStatus } from "@/helpers/waves/approve-wave.helpers";
import { getApprovalWindowEndTime } from "@/helpers/waves/approve-wave.helpers";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import useDeviceInfo from "@/hooks/useDeviceInfo";
interface WaveApprovalStatusBarProps {
  readonly approvedCount: number | null;
  readonly closeStatus: ApprovalWaveCloseStatus;
  readonly isApprovalCountError?: boolean | undefined;
  readonly isApprovalStatusError?: boolean | undefined;
  readonly retryApprovalCount?: (() => void) | null | undefined;
  readonly retryApprovalStatus?: (() => void) | null | undefined;
  readonly wave: ApiWave;
}
const formatTimeLeft = (targetTime: number, currentMillis: number): string => {
  const timeLeft = calculateTimeLeft(targetTime, currentMillis);
  if (timeLeft.days > 0) {
    return `${timeLeft.days}d ${timeLeft.hours}h left`;
  }
  if (timeLeft.hours > 0) {
    return `${timeLeft.hours}h ${timeLeft.minutes}m left`;
  }
  return `${timeLeft.minutes}m left`;
};
const MINUTE_IN_MS = 60 * 1000;
const MINUTES_IN_HOUR = 60;
const formatDurationCore = (durationMs: number | null | undefined): string | null => {
  if (typeof durationMs !== "number" || !Number.isFinite(durationMs) || durationMs <= 0) {
    return null;
  }
  const totalMinutes = Math.floor(durationMs / MINUTE_IN_MS);
  if (totalMinutes <= 0) {
    return "<1m";
  }
  const hours = Math.floor(totalMinutes / MINUTES_IN_HOUR);
  const minutes = totalMinutes % MINUTES_IN_HOUR;
  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  return `${minutes}m`;
};
const formatWinningThresholdMinDuration = (durationMs: number | null | undefined): string => formatDurationCore(durationMs) ?? "Immediate";
const formatTimeWeightedDuration = (durationMs: number | null | undefined): string | null => formatDurationCore(durationMs);
const getCurrentMillisForStatusRender = (_clockTick: number, _endTime: number | null, _closeStatus: ApprovalWaveCloseStatus): number => Time.currentMillis();
const approvalStatusErrorMessage = "Unable to check approval status. " + "Voting and create controls are paused until the check succeeds.";
const approvalCountErrorMessage = "Unable to load approved count.";
const APPROVAL_RULES_TITLE = "Approval rules";
const FALLBACK_CREDIT_LABEL = "credit";
const subscribeToClientRender = () => () => {};
const getClientRenderSnapshot = () => true;
const getServerRenderSnapshot = () => false;
interface ApprovalStatusItemProps {
  readonly label: string;
  readonly value: string;
  readonly valueClassName?: string | undefined;
}
const ApprovalStatusItem: FC<ApprovalStatusItemProps> = t0 => {
  const $ = _c(8);
  const {
    label,
    value,
    valueClassName: t1
  } = t0;
  const valueClassName = t1 === undefined ? "tw-text-iron-100" : t1;
  let t2;
  if ($[0] !== label) {
    t2 = <span className="tw-whitespace-nowrap tw-text-xs tw-font-medium tw-text-iron-500">{label}</span>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `tw-min-w-0 tw-text-sm tw-font-semibold ${valueClassName}`;
  let t4;
  if ($[2] !== t3 || $[3] !== value) {
    t4 = <span className={t3}>{value}</span>;
    $[2] = t3;
    $[3] = value;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <div className="tw-flex tw-min-w-0 tw-flex-col tw-items-center tw-gap-1 tw-text-center tw-leading-5">{t2}{t4}</div>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};
interface ApprovalRulesHelpProps {
  readonly creditNoun: string;
  readonly holdTimeLabel: string;
  readonly isTimeWeighted: boolean;
  readonly scoringDurationLabel: string | null;
  readonly requiresHoldTime: boolean;
  readonly thresholdIsSet: boolean;
  readonly thresholdLabel: string;
}
const ApprovalRulesHelp: FC<ApprovalRulesHelpProps> = t0 => {
  const $ = _c(23);
  const {
    creditNoun,
    holdTimeLabel,
    isTimeWeighted,
    scoringDurationLabel,
    requiresHoldTime,
    thresholdIsSet,
    thresholdLabel
  } = t0;
  let t1;
  if ($[0] !== creditNoun || $[1] !== holdTimeLabel || $[2] !== isTimeWeighted || $[3] !== requiresHoldTime || $[4] !== thresholdIsSet || $[5] !== thresholdLabel) {
    t1 = <div className="tw-space-y-2">{thresholdIsSet ? <p className="tw-mb-0">This wave uses {creditNoun}.{" "}{isTimeWeighted && requiresHoldTime ? <>A drop is approved when its time-weighted score reaches{" "}{thresholdLabel} {creditNoun} and keeps at least that much credit{" "}for {holdTimeLabel}.</> : isTimeWeighted ? <>A drop is approved when its time-weighted score reaches{" "}{thresholdLabel} {creditNoun}.</> : requiresHoldTime ? <>A drop is approved when it reaches {thresholdLabel} {creditNoun}{" "}and keeps at least that much credit for {holdTimeLabel}.</> : <>A drop is approved as soon as it reaches {thresholdLabel}{" "}{creditNoun}.</>}</p> : <p className="tw-mb-0">This wave has no credit threshold set yet.</p>}</div>;
    $[0] = creditNoun;
    $[1] = holdTimeLabel;
    $[2] = isTimeWeighted;
    $[3] = requiresHoldTime;
    $[4] = thresholdIsSet;
    $[5] = thresholdLabel;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <dt className="tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-500">Credit needed</dt>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== creditNoun) {
    t3 = <div>{t2}<dd className="tw-mb-0 tw-mt-1 tw-text-iron-200">How much {creditNoun} a drop must reach before it can be approved.</dd></div>;
    $[8] = creditNoun;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <dt className="tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-500">Vote scoring</dt>;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== isTimeWeighted || $[12] !== scoringDurationLabel) {
    t5 = <div>{t4}<dd className="tw-mb-0 tw-mt-1 tw-text-iron-200">{isTimeWeighted && scoringDurationLabel !== null ? <>Approval uses a {scoringDurationLabel} average. New votes gain{" "}influence gradually, so votes given now can be higher than the approval score.</> : <>Votes count immediately in the approval score.</>}</dd></div>;
    $[11] = isTimeWeighted;
    $[12] = scoringDurationLabel;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  let t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div><dt className="tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-500">Hold time</dt><dd className="tw-mb-0 tw-mt-1 tw-text-iron-200">How long a drop must stay at or above the credit needed. If it falls below, the timer starts again.</dd></div>;
    t7 = <div><dt className="tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-500">Approved drops</dt><dd className="tw-mb-0 tw-mt-1 tw-text-iron-200">How many drops are already approved. If there is a cap, this shows approved / max.</dd></div>;
    t8 = <div><dt className="tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-500">Approval window</dt><dd className="tw-mb-0 tw-mt-1 tw-text-iron-200">Open means drops can still be approved. It closes when the end time passes or the max approvals are reached.</dd></div>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t3 || $[18] !== t5) {
    t9 = <dl className="tw-mb-0 tw-mt-4 tw-space-y-3">{t3}{t5}{t6}{t7}{t8}</dl>;
    $[17] = t3;
    $[18] = t5;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t1 || $[21] !== t9) {
    t10 = <div className="tw-w-80 tw-max-w-[calc(100vw-2rem)] tw-text-sm tw-leading-5 tw-text-iron-200">{t1}{t9}</div>;
    $[20] = t1;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
};
interface ApprovalRulesButtonProps {
  readonly "aria-controls"?: string | undefined;
  readonly "aria-expanded"?: boolean | undefined;
  readonly "aria-haspopup"?: "dialog" | undefined;
  readonly onClick?: (() => void) | undefined;
}
const ApprovalRulesButton: FC<ApprovalRulesButtonProps> = t0 => {
  const $ = _c(6);
  const {
    "aria-controls": ariaControls,
    "aria-expanded": ariaExpanded,
    "aria-haspopup": ariaHasPopup,
    onClick
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FontAwesomeIcon icon={faInfoCircle} className="tw-size-3.5" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== ariaControls || $[2] !== ariaExpanded || $[3] !== ariaHasPopup || $[4] !== onClick) {
    t2 = <button type="button" aria-label={APPROVAL_RULES_TITLE} aria-controls={ariaControls} aria-expanded={ariaExpanded} aria-haspopup={ariaHasPopup} onClick={onClick} className="tw-inline-flex tw-size-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-p-0 tw-text-iron-400 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-iron-300 desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-text-iron-200">{t1}</button>;
    $[1] = ariaControls;
    $[2] = ariaExpanded;
    $[3] = ariaHasPopup;
    $[4] = onClick;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export default function WaveApprovalStatusBar(t0) {
  const $ = _c(74);
  const {
    approvedCount,
    closeStatus,
    isApprovalCountError: t1,
    isApprovalStatusError: t2,
    retryApprovalCount: t3,
    retryApprovalStatus: t4,
    wave
  } = t0;
  const isApprovalCountError = t1 === undefined ? false : t1;
  const isApprovalStatusError = t2 === undefined ? false : t2;
  const retryApprovalCount = t3 === undefined ? null : t3;
  const retryApprovalStatus = t4 === undefined ? null : t4;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const isClientHydrated = useSyncExternalStore(subscribeToClientRender, getClientRenderSnapshot, getServerRenderSnapshot);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const approvalRulesHelpId = useId();
  const winningThreshold = wave.wave.winning_threshold;
  const winningThresholdMinDuration = wave.wave.winning_threshold_min_duration_ms;
  let t5;
  if ($[0] !== wave.wave.time_lock_ms) {
    t5 = formatTimeWeightedDuration(wave.wave.time_lock_ms);
    $[0] = wave.wave.time_lock_ms;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const timeWeightedDurationLabel = t5;
  const isTimeWeighted = timeWeightedDurationLabel !== null;
  const maxWinners = wave.wave.max_winners;
  let t6;
  if ($[2] !== wave) {
    t6 = getApprovalWindowEndTime(wave);
    $[2] = wave;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const endTime = t6;
  const [clockTick, setClockTick] = useState(0);
  let t7;
  if ($[4] !== clockTick || $[5] !== closeStatus || $[6] !== endTime) {
    t7 = getCurrentMillisForStatusRender(clockTick, endTime, closeStatus);
    $[4] = clockTick;
    $[5] = closeStatus;
    $[6] = endTime;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const currentMillis = t7;
  const isCountOnlyError = isApprovalCountError && !isApprovalStatusError;
  let t8;
  let t9;
  if ($[8] !== closeStatus || $[9] !== endTime) {
    t8 = () => {
      if (closeStatus !== null || endTime === null) {
        return;
      }
      const intervalId = globalThis.setInterval(() => {
        setClockTick(_temp);
      }, 1000);
      return () => {
        globalThis.clearInterval(intervalId);
      };
    };
    t9 = [closeStatus, endTime];
    $[8] = closeStatus;
    $[9] = endTime;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useEffect(t8, t9);
  let t10;
  if ($[12] !== winningThreshold) {
    t10 = typeof winningThreshold === "number" && Number.isFinite(winningThreshold) ? formatNumberWithCommas(winningThreshold) : "Not set";
    $[12] = winningThreshold;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const thresholdLabel = t10;
  const thresholdIsSet = thresholdLabel !== "Not set";
  let t11;
  if ($[14] !== winningThresholdMinDuration) {
    t11 = formatWinningThresholdMinDuration(winningThresholdMinDuration);
    $[14] = winningThresholdMinDuration;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const thresholdMinDurationLabel = t11;
  let t12;
  if ($[16] !== winningThresholdMinDuration) {
    t12 = typeof winningThresholdMinDuration === "number" && Number.isFinite(winningThresholdMinDuration) && winningThresholdMinDuration > 0;
    $[16] = winningThresholdMinDuration;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const requiresHoldTime = t12;
  const creditLabel = WAVE_VOTING_LABELS[wave.voting.credit_type] ?? FALLBACK_CREDIT_LABEL;
  const creditNoun = creditLabel === FALLBACK_CREDIT_LABEL ? FALLBACK_CREDIT_LABEL : `${creditLabel} credit`;
  const thresholdValueLabel = thresholdIsSet ? `${thresholdLabel} ${creditLabel}` : thresholdLabel;
  let t13;
  bb0: {
    if (isApprovalStatusError || isCountOnlyError) {
      t13 = "Unavailable";
      break bb0;
    }
    if (approvedCount === null) {
      t13 = "Checking";
      break bb0;
    }
    if (typeof maxWinners === "number" && Number.isFinite(maxWinners)) {
      let t14;
      if ($[18] !== approvedCount) {
        t14 = formatNumberWithCommas(approvedCount);
        $[18] = approvedCount;
        $[19] = t14;
      } else {
        t14 = $[19];
      }
      const approved = t14;
      let t15;
      if ($[20] !== maxWinners) {
        t15 = formatNumberWithCommas(maxWinners);
        $[20] = maxWinners;
        $[21] = t15;
      } else {
        t15 = $[21];
      }
      const max = t15;
      t13 = `${approved} / ${max}`;
      break bb0;
    }
    let t14;
    if ($[22] !== approvedCount) {
      t14 = formatNumberWithCommas(approvedCount);
      $[22] = approvedCount;
      $[23] = t14;
    } else {
      t14 = $[23];
    }
    t13 = t14;
  }
  const approvedLabel = t13;
  let statusLabel;
  if (closeStatus === "max_reached") {
    statusLabel = "Max approvals reached";
  } else {
    if (closeStatus === "ended") {
      statusLabel = "Approval window ended";
    } else {
      if (isApprovalStatusError) {
        statusLabel = "Unable to check approvals";
      } else {
        if (approvedCount === null && !isCountOnlyError) {
          statusLabel = "Checking";
        } else {
          if (endTime !== null) {
            let t14;
            if ($[24] !== currentMillis || $[25] !== endTime) {
              t14 = formatTimeLeft(endTime, currentMillis);
              $[24] = currentMillis;
              $[25] = endTime;
              $[26] = t14;
            } else {
              t14 = $[26];
            }
            statusLabel = t14;
          } else {
            statusLabel = "Open";
          }
        }
      }
    }
  }
  let statusTextClassName = "tw-text-amber-200";
  if (isApprovalStatusError) {
    statusTextClassName = "tw-text-rose-200";
  } else {
    if (closeStatus === null) {
      statusTextClassName = "tw-text-iron-100";
    }
  }
  let t14;
  bb1: {
    if (isApprovalStatusError) {
      t14 = approvalStatusErrorMessage;
      break bb1;
    }
    if (isCountOnlyError) {
      t14 = approvalCountErrorMessage;
      break bb1;
    }
    t14 = null;
  }
  const errorMessage = t14;
  let t15;
  bb2: {
    if (isApprovalStatusError) {
      t15 = retryApprovalStatus;
      break bb2;
    }
    if (isCountOnlyError) {
      t15 = retryApprovalCount;
      break bb2;
    }
    t15 = null;
  }
  const retryError = t15;
  let t16;
  if ($[27] !== creditNoun || $[28] !== isTimeWeighted || $[29] !== requiresHoldTime || $[30] !== thresholdIsSet || $[31] !== thresholdLabel || $[32] !== thresholdMinDurationLabel || $[33] !== timeWeightedDurationLabel) {
    t16 = <ApprovalRulesHelp creditNoun={creditNoun} holdTimeLabel={thresholdMinDurationLabel} isTimeWeighted={isTimeWeighted} scoringDurationLabel={timeWeightedDurationLabel} requiresHoldTime={requiresHoldTime} thresholdIsSet={thresholdIsSet} thresholdLabel={thresholdLabel} />;
    $[27] = creditNoun;
    $[28] = isTimeWeighted;
    $[29] = requiresHoldTime;
    $[30] = thresholdIsSet;
    $[31] = thresholdLabel;
    $[32] = thresholdMinDurationLabel;
    $[33] = timeWeightedDurationLabel;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const approvalRulesHelp = t16;
  const useMobileHelp = isClientHydrated && hasTouchScreen;
  let t17;
  if ($[35] !== approvalRulesHelp || $[36] !== approvalRulesHelpId || $[37] !== isHelpOpen || $[38] !== useMobileHelp) {
    t17 = useMobileHelp ? <ApprovalRulesButton aria-controls={isHelpOpen ? approvalRulesHelpId : undefined} aria-expanded={isHelpOpen} aria-haspopup="dialog" onClick={() => setIsHelpOpen(true)} /> : <HoverCard content={approvalRulesHelp} ariaLabel={APPROVAL_RULES_TITLE} placement="left" delayShow={300} delayHide={0} offset={8}><ApprovalRulesButton aria-haspopup="dialog" /></HoverCard>;
    $[35] = approvalRulesHelp;
    $[36] = approvalRulesHelpId;
    $[37] = isHelpOpen;
    $[38] = useMobileHelp;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const approvalRulesButton = t17;
  let t18;
  if ($[40] !== thresholdValueLabel) {
    t18 = <ApprovalStatusItem label="Credit needed" value={thresholdValueLabel} />;
    $[40] = thresholdValueLabel;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== thresholdMinDurationLabel) {
    t19 = <ApprovalStatusItem label="Hold time" value={thresholdMinDurationLabel} />;
    $[42] = thresholdMinDurationLabel;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== approvedLabel) {
    t20 = <ApprovalStatusItem label="Approved drops" value={approvedLabel} />;
    $[44] = approvedLabel;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== statusLabel || $[47] !== statusTextClassName) {
    t21 = <ApprovalStatusItem label="Approval window" value={statusLabel} valueClassName={statusTextClassName} />;
    $[46] = statusLabel;
    $[47] = statusTextClassName;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t18 || $[50] !== t19 || $[51] !== t20 || $[52] !== t21) {
    t22 = <div className="tw-grid tw-grid-cols-2 tw-items-center tw-gap-x-4 tw-gap-y-1 md:tw-grid-cols-4">{t18}{t19}{t20}{t21}</div>;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== timeWeightedDurationLabel) {
    t23 = timeWeightedDurationLabel !== null && <p className="tw-mb-0 tw-mt-2 tw-border-0 tw-border-t tw-border-solid tw-border-iron-800 tw-pt-2 tw-text-xs tw-font-medium tw-leading-5 tw-text-iron-400">Time-weighted scoring is on: approval uses a{" "}{timeWeightedDurationLabel} average, not the raw votes-given-now{" "}total.</p>;
    $[54] = timeWeightedDurationLabel;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== errorMessage || $[57] !== retryError) {
    t24 = errorMessage && <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"><p className="tw-mb-0 tw-text-xs tw-font-medium tw-text-iron-400">{errorMessage}</p>{retryError && <button type="button" onClick={retryError} className="tw-inline-flex tw-w-fit tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-600 tw-bg-iron-900 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-iron-100 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-iron-300 desktop-hover:hover:tw-border-iron-400 desktop-hover:hover:tw-bg-iron-800">Try again</button>}</div>;
    $[56] = errorMessage;
    $[57] = retryError;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t22 || $[60] !== t23 || $[61] !== t24) {
    t25 = <div role="group" aria-label="Approval status" className="tw-min-w-0 tw-flex-1 tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-px-3 tw-py-2">{t22}{t23}{t24}</div>;
    $[59] = t22;
    $[60] = t23;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== approvalRulesButton) {
    t26 = <div className="tw-flex tw-shrink-0 tw-items-center">{approvalRulesButton}</div>;
    $[63] = approvalRulesButton;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== approvalRulesHelp || $[66] !== approvalRulesHelpId || $[67] !== isHelpOpen || $[68] !== useMobileHelp) {
    t27 = useMobileHelp && <MobileWrapperDialog isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} title={APPROVAL_RULES_TITLE}><div id={approvalRulesHelpId} className="tw-px-4 tw-pb-2 tw-pt-3">{approvalRulesHelp}</div></MobileWrapperDialog>;
    $[65] = approvalRulesHelp;
    $[66] = approvalRulesHelpId;
    $[67] = isHelpOpen;
    $[68] = useMobileHelp;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== t25 || $[71] !== t26 || $[72] !== t27) {
    t28 = <div className="tw-mt-2 tw-flex tw-flex-none tw-items-stretch tw-gap-2 md:tw-mt-3">{t25}{t26}{t27}</div>;
    $[70] = t25;
    $[71] = t26;
    $[72] = t27;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  return t28;
}
function _temp(tick) {
  return tick + 1;
}